import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { VideoIcon, Play, Square, Save, Download, Brain, Activity, Eye } from 'lucide-react';
import { edgeInvoke } from '@/lib/edgeInvoke';
import { listMyVideos, VideoRecord } from '@/lib/videos';
import { useToast } from '@/hooks/use-toast';
import { DiagnosticsHeader } from '@/components/tests/DiagnosticsHeader';
import { LogBox } from '@/components/tests/LogBox';
import { AnalysisComparisonGrid } from '@/components/tests/AnalysisComparisonGrid';
import { useAnalysisComparison } from '@/hooks/use-analysis-comparison';
import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

// Extend Worker interface to include cleanup function
interface ExtendedWorker extends Worker {
  cleanup?: () => void;
}

interface PoseFrame {
  frameIndex: number;
  timeS: number;
  landmarks: Array<{ x: number; y: number; z?: number; visibility?: number }>;
}

interface PoseLandmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

// Golf-relevant pose connections for skeleton drawing
const GOLF_POSE_CONNECTIONS = [
  [11, 12], // shoulders
  [11, 13], [13, 15], // left arm
  [12, 14], [14, 16], // right arm
  [11, 23], [12, 24], // torso
  [23, 24], // hips
  [23, 25], [25, 27], // left leg
  [24, 26], [26, 28], // right leg
];

export default function TestPose() {
  const { toast } = useToast();
  const { 
    runAllAnalyses, 
    exportResults, 
    clearResults, 
    isAnalyzing, 
    results 
  } = useAnalysisComparison();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  
  // UI State
  const [playbackId, setPlaybackId] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<VideoRecord | null>(null);
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPoseRunning, setIsPoseRunning] = useState(false);
  const [targetFPS, setTargetFPS] = useState(30);
  const [drawSkeleton, setDrawSkeleton] = useState(true);
  const [workerReady, setWorkerReady] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('Initializing...');
  const [initProgress, setInitProgress] = useState(0);
  const [initStage, setInitStage] = useState<string>('starting');
  const [initError, setInitError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Data State
  const [capturedFrames, setCapturedFrames] = useState<PoseFrame[]>([]);
  const [processingStats, setProcessingStats] = useState({
    framesCaptured: 0,
    actualFPS: 0,
    modelLoadMs: 0,
    avgLatencyMs: 0
  });
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [savedAnalysisId, setSavedAnalysisId] = useState<string>('');
  
  // Animation frame tracking
  const animationFrameRef = useRef<number>();
  const lastCaptureTimeRef = useRef<number>(0);
  const processingTimesRef = useRef<number[]>([]);
  const frameIndexRef = useRef<number>(0);
  const fpsCounterRef = useRef<number>(0);
  const fpsStartTimeRef = useRef<number>(0);
  
  // MediaPipe state (main thread)
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const [mediaPipeReady, setMediaPipeReady] = useState(false);

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-20), `[${timestamp}] ${message}`]);
    console.log(`[TestPose] ${message}`);
  }, []);

  const updateProcessingStatus = useCallback(() => {
    if (initError) {
      setProcessingStatus(`Initialization failed: ${initError}`);
    } else if (!mediaPipeReady) {
      switch (initStage) {
        case 'wasm':
          setProcessingStatus('Downloading MediaPipe WASM files...');
          break;
        case 'model':
          setProcessingStatus('Downloading pose detection model...');
          break;
        case 'complete':
          setProcessingStatus('MediaPipe ready!');
          break;
        default:
          setProcessingStatus('Initializing MediaPipe...');
      }
    } else if (!videoReady) {
      setProcessingStatus('MediaPipe ready - Load a video to analyze');
    } else {
      setProcessingStatus('Ready to analyze poses');
    }
  }, [mediaPipeReady, videoReady, initStage, initError]);

  // Update status when dependencies change
  useEffect(() => {
    updateProcessingStatus();
  }, [updateProcessingStatus]);

  // Load user videos on mount
  useEffect(() => {
    const loadVideos = async () => {
      try {
        const { data, error } = await listMyVideos(20);
        if (error) {
          addLog(`Error loading videos: ${error.message}`);
          return;
        }
        setVideos(data || []);
        addLog(`Loaded ${data?.length || 0} videos from library`);
      } catch (error) {
        addLog(`Failed to load videos: ${error}`);
      }
    };
    loadVideos();
  }, [addLog]);

  // Initialize MediaPipe in main thread
  useEffect(() => {
    const initMediaPipe = async () => {
      try {
        addLog('Initializing MediaPipe... (this may take 15-30 seconds)');
        setInitStage('wasm');
        setInitProgress(20);
        
        // Step 1: Download WASM files
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );
        
        setInitStage('model');
        setInitProgress(60);
        addLog('Downloading pose detection model...');
        
        // Step 2: Create pose landmarker
        const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: "GPU"
          },
          runningMode: "IMAGE",
          numPoses: 1,
          minPoseDetectionConfidence: 0.5,
          minPosePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
          outputSegmentationMasks: false
        });
        
        poseLandmarkerRef.current = poseLandmarker;
        setMediaPipeReady(true);
        setInitProgress(100);
        setInitStage('complete');
        setInitError(null);
        addLog('MediaPipe initialized successfully');
        
      } catch (error) {
        setInitError(error.message);
        setInitProgress(0);
        addLog(`MediaPipe initialization failed: ${error.message}`);
      }
    };
    
    initMediaPipe();
  }, [addLog]);

  // Initialize simple frame worker
  useEffect(() => {
    const initWorker = async () => {
      try {
        workerRef.current = new Worker(
          new URL('../workers/poseWorker.ts', import.meta.url),
          { type: 'module' }
        );
        
        workerRef.current.onmessage = (e) => {
          const { type, frameIndex, timeS, imageData, error } = e.data;
          
          if (type === 'INIT_COMPLETE') {
            setWorkerReady(true);
            addLog('Frame extraction worker ready');
          } else if (type === 'FRAME_EXTRACTED') {
            processFrameWithMediaPipe(frameIndex, timeS, imageData);
          } else if (type === 'ERROR') {
            addLog(`Worker error: ${error}`);
          }
        };
        
        workerRef.current.postMessage({ type: 'INIT' });
        
      } catch (error) {
        addLog(`Failed to initialize frame worker: ${error}`);
      }
    };
    
    initWorker();
    
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [addLog]);

  const retryInitialization = useCallback(() => {
    setIsRetrying(true);
    setInitError(null);
    setInitProgress(0);
    setInitStage('starting');
    setMediaPipeReady(false);
    
    // Retry MediaPipe initialization
    setTimeout(async () => {
      try {
        addLog('Retrying MediaPipe initialization...');
        setInitStage('wasm');
        setInitProgress(20);
        
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );
        
        setInitStage('model');
        setInitProgress(60);
        addLog('Downloading pose detection model...');
        
        const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: "GPU"
          },
          runningMode: "IMAGE",
          numPoses: 1,
          minPoseDetectionConfidence: 0.5,
          minPosePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
          outputSegmentationMasks: false
        });
        
        poseLandmarkerRef.current = poseLandmarker;
        setMediaPipeReady(true);
        setInitProgress(100);
        setInitStage('complete');
        setInitError(null);
        setIsRetrying(false);
        addLog('MediaPipe retry successful');
        
      } catch (error) {
        setInitError(error.message);
        setInitProgress(0);
        setIsRetrying(false);
        addLog(`Retry failed: ${error.message}`);
      }
    }, 1000);
  }, [addLog]);

  const processFrameWithMediaPipe = useCallback(async (frameIndex: number, timeS: number, imageData: ImageData) => {
    if (!poseLandmarkerRef.current) {
      addLog('MediaPipe not ready for frame processing');
      return;
    }

    try {
      const startTime = performance.now();
      
      // Create canvas for MediaPipe processing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      ctx.putImageData(imageData, 0, 0);
      
      // Detect pose landmarks
      const result = poseLandmarkerRef.current.detect(canvas);
      
      // Extract landmarks
      let landmarks: Array<{ x: number; y: number; z?: number; visibility?: number }> = [];
      
      if (result.landmarks && result.landmarks.length > 0) {
        landmarks = result.landmarks[0].map(landmark => ({
          x: landmark.x,
          y: landmark.y,
          z: landmark.z,
          visibility: landmark.visibility
        }));
      }
      
      const poseFrame: PoseFrame = {
        frameIndex,
        timeS,
        landmarks
      };
      
      const processingTime = performance.now() - startTime;
      handleFrameProcessed(poseFrame);
      
      // Log first few frames for debugging
      if (frameIndex < 3) {
        addLog(`Processed frame ${frameIndex}: ${landmarks.length} landmarks detected in ${processingTime.toFixed(1)}ms`);
      }
      
    } catch (error) {
      addLog(`Error processing frame ${frameIndex}: ${error.message}`);
    }
  }, [addLog]);

  const handleFrameProcessed = useCallback((frameData: PoseFrame) => {
    setCapturedFrames(prev => [...prev, frameData]);
    
    // Update processing stats
    const processingTime = performance.now() - lastCaptureTimeRef.current;
    processingTimesRef.current.push(processingTime);
    
    if (processingTimesRef.current.length > 30) {
      processingTimesRef.current = processingTimesRef.current.slice(-30);
    }
    
    const avgLatency = processingTimesRef.current.reduce((a, b) => a + b, 0) / processingTimesRef.current.length;
    
    setProcessingStats(prev => ({
      ...prev,
      framesCaptured: prev.framesCaptured + 1,
      avgLatencyMs: avgLatency
    }));
    
    // Draw skeleton overlay if enabled
    if (drawSkeleton && canvasRef.current && videoRef.current) {
      drawPoseSkeleton(frameData.landmarks);
    }
  }, [drawSkeleton]);

  const drawPoseSkeleton = useCallback((landmarks: PoseLandmark[]) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || landmarks.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set drawing style
    ctx.strokeStyle = '#22c55e';
    ctx.fillStyle = '#22c55e';
    ctx.lineWidth = 2;
    
    // Draw connections
    GOLF_POSE_CONNECTIONS.forEach(([start, end]) => {
      const startPoint = landmarks[start];
      const endPoint = landmarks[end];
      
      if (startPoint && endPoint && 
          startPoint.visibility && startPoint.visibility > 0.5 &&
          endPoint.visibility && endPoint.visibility > 0.5) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x * canvas.width, startPoint.y * canvas.height);
        ctx.lineTo(endPoint.x * canvas.width, endPoint.y * canvas.height);
        ctx.stroke();
      }
    });
    
    // Draw key points
    landmarks.forEach((landmark, index) => {
      if (landmark.visibility && landmark.visibility > 0.5) {
        const isKeyJoint = [11, 12, 13, 14, 15, 16, 23, 24, 25, 26].includes(index);
        ctx.beginPath();
        ctx.arc(
          landmark.x * canvas.width,
          landmark.y * canvas.height,
          isKeyJoint ? 4 : 2,
          0,
          2 * Math.PI
        );
        ctx.fill();
      }
    });
  }, []);

  const loadVideoFromLibrary = async (video: VideoRecord) => {
    if (!video.mux_playback_id) {
      toast({
        title: "Error",
        description: "Video does not have a playback ID",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedVideo(video);
    setPlaybackId(video.mux_playback_id);
    addLog(`Selected video: ${video.mux_playback_id}`);
  };

  const signAndLoadVideo = async () => {
    if (!playbackId.trim()) {
      toast({
        title: "Error", 
        description: "Please enter a playback ID",
        variant: "destructive"
      });
      return;
    }
    
    try {
      addLog(`Signing playback URL for: ${playbackId}`);
      
      const result = await edgeInvoke('playback-url', {
        playback_id: playbackId,
        ttl_seconds: 1800 // 30 minutes
      });
      
      if (!result.url) {
        throw new Error('No signed URL returned');
      }
      
      const video = videoRef.current;
      if (!video) return;
      
      video.src = result.url;
      video.load();
      
      // Set up video event handlers
      video.onloadedmetadata = () => {
        setIsVideoLoaded(true);
        setVideoReady(true);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        updateProcessingStatus();
        addLog(`Video loaded: ${video.videoWidth}x${video.videoHeight}, duration: ${video.duration}s`);
      };
      
      video.onerror = () => {
        addLog(`Video load error: ${video.error?.message || 'Unknown error'}`);
        setIsVideoLoaded(false);
      };
      
    } catch (error) {
      addLog(`Failed to sign and load video: ${error}`);
      toast({
        title: "Error",
        description: "Failed to load video",
        variant: "destructive"
      });
    }
  };

  // Frame capture function with comprehensive logging and robustness
  const captureFrame = useCallback(() => {
    if (!isPoseRunning || !isVideoLoaded || !workerRef.current || !mediaPipeReady) {
      addLog(`Capture blocked - Running: ${isPoseRunning}, VideoLoaded: ${isVideoLoaded}, Worker: ${!!workerRef.current}, MediaPipe: ${mediaPipeReady}`);
      return;
    }
    
    const video = videoRef.current;
    if (!video) {
      addLog('Video element not available');
      return;
    }

    // Comprehensive video state logging
    const videoState = {
      currentTime: video.currentTime,
      duration: video.duration,
      paused: video.paused,
      ended: video.ended,
      readyState: video.readyState,
      networkState: video.networkState,
      playbackRate: video.playbackRate
    };

    // Log video state every 30 frames for debugging
    if (frameIndexRef.current % 30 === 0) {
      addLog(`Video state - Time: ${videoState.currentTime.toFixed(2)}s/${videoState.duration.toFixed(2)}s, Paused: ${videoState.paused}, Ended: ${videoState.ended}, ReadyState: ${videoState.readyState}`);
    }
    
    // Check if video is still playing
    if (video.paused) {
      addLog(`Video paused at ${video.currentTime.toFixed(2)}s during analysis - attempting to resume`);
      // Try to resume playback
      video.play().catch(error => {
        addLog(`Failed to resume video: ${error.message}`);
        setIsPoseRunning(false);
      });
      return;
    }
    
    if (video.ended) {
      addLog(`Video ended at ${video.currentTime.toFixed(2)}s - stopping capture. Total frames: ${frameIndexRef.current}`);
      setIsPoseRunning(false);
      return;
    }

    // Check if video has reached near end (safety check)
    if (video.currentTime >= video.duration - 0.1) {
      addLog(`Video near end (${video.currentTime.toFixed(2)}s >= ${(video.duration - 0.1).toFixed(2)}s) - stopping capture`);
      setIsPoseRunning(false);
      return;
    }
    
    const now = performance.now();
    const captureInterval = 1000 / targetFPS;
    
    if (now - lastCaptureTimeRef.current >= captureInterval) {
      // Create canvas for frame capture
      const captureCanvas = document.createElement('canvas');
      const ctx = captureCanvas.getContext('2d');
      if (!ctx) {
        addLog('Failed to get canvas context for frame capture');
        return;
      }
      
      // Downscale for processing efficiency
      const targetWidth = 480;
      const aspectRatio = video.videoHeight / video.videoWidth;
      const targetHeight = Math.round(targetWidth * aspectRatio);
      
      captureCanvas.width = targetWidth;
      captureCanvas.height = targetHeight;
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      
      // Get image data and send to worker
      const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
      
      // Enhanced logging for debugging
      if (frameIndexRef.current < 5 || frameIndexRef.current % 30 === 0) {
        addLog(`Capturing frame ${frameIndexRef.current}: ${targetWidth}x${targetHeight} at time ${video.currentTime.toFixed(3)}s`);
      }
      
      workerRef.current.postMessage({
        type: 'EXTRACT_FRAME',
        frameIndex: frameIndexRef.current++,
        timeS: video.currentTime,
        imageData
      });
      
      lastCaptureTimeRef.current = now;
      fpsCounterRef.current++;
      
      // Update FPS calculation
      if (now - fpsStartTimeRef.current >= 1000) {
        const actualFPS = (fpsCounterRef.current * 1000) / (now - fpsStartTimeRef.current);
        setProcessingStats(prev => ({ ...prev, actualFPS }));
        addLog(`FPS Update: Target ${targetFPS}, Actual ${actualFPS.toFixed(1)}, Frames captured: ${frameIndexRef.current}`);
        fpsCounterRef.current = 0;
        fpsStartTimeRef.current = now;
      }
    }
    
    // Continue animation loop
    if (isPoseRunning) {
      animationFrameRef.current = requestAnimationFrame(captureFrame);
    }
  }, [isPoseRunning, isVideoLoaded, mediaPipeReady, targetFPS, addLog]);

  // Effect to manage frame capture loop
  useEffect(() => {
    if (isPoseRunning && isVideoLoaded && mediaPipeReady && workerReady) {
      addLog('Starting frame capture loop...');
      frameIndexRef.current = 0;
      fpsCounterRef.current = 0;
      fpsStartTimeRef.current = performance.now();
      lastCaptureTimeRef.current = performance.now();
      
      // Start the animation loop
      animationFrameRef.current = requestAnimationFrame(captureFrame);
      
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else if (!isPoseRunning && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, [isPoseRunning, isVideoLoaded, mediaPipeReady, workerReady, captureFrame]);

  const startPoseAnalysis = useCallback(async () => {
    if (!isVideoLoaded || !workerRef.current) {
      addLog(`Analysis blocked - Video loaded: ${isVideoLoaded}, Worker: ${!!workerRef.current}, Worker ready: ${workerReady}`);
      toast({
        title: "Error",
        description: "Video not loaded or worker not ready",
        variant: "destructive"
      });
      return;
    }
    
    if (!mediaPipeReady) {
      addLog("MediaPipe not ready for frame processing");
      toast({
        title: "Error", 
        description: "MediaPipe is still initializing",
        variant: "destructive"
      });
      return;
    }

    const video = videoRef.current;
    if (!video) {
      addLog('Video element not available');
      return;
    }

    // Reset video to beginning for consistent capture
    video.currentTime = 0;
    addLog(`Reset video to beginning. Duration: ${video.duration}s`);
    
    // Set up video event listeners for debugging
    const onPlay = () => addLog('Video play event triggered');
    const onPause = () => addLog('Video pause event triggered');
    const onEnded = () => addLog('Video ended event triggered');
    const onTimeUpdate = () => {
      if (frameIndexRef.current % 50 === 0) { // Log every 50 frames
        addLog(`Video time update: ${video.currentTime.toFixed(2)}s`);
      }
    };
    
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('timeupdate', onTimeUpdate);

    // Auto-play video when starting analysis
    try {
      if (video.paused) {
        addLog('Video is paused, attempting to auto-play...');
        await video.play();
        addLog(`Video auto-play successful. Playing: ${!video.paused}, Current time: ${video.currentTime}s`);
      }
      
      // Force video to loop for testing
      video.loop = true;
      addLog('Video loop enabled for continuous capture testing');
      
    } catch (error) {
      addLog(`Failed to auto-play video: ${error.message}`);
      toast({
        title: "Playback Required",
        description: "Please manually play the video to start analysis",
        variant: "default"
      });
      
      // Cleanup event listeners
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('timeupdate', onTimeUpdate);
      return;
    }
    
    setCapturedFrames([]);
    setProcessingStats(prev => ({ ...prev, framesCaptured: 0, actualFPS: 0 }));
    addLog(`Starting pose analysis at ${targetFPS} FPS. Video duration: ${video.duration}s`);
    
    // This will trigger the useEffect above to start frame capture
    setIsPoseRunning(true);
  }, [isVideoLoaded, targetFPS, mediaPipeReady, workerReady, addLog, toast]);

  const stopPoseAnalysis = () => {
    setIsPoseRunning(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    addLog(`Stopped pose analysis. Captured ${capturedFrames.length} frames`);
  };

  const saveToDB = async () => {
    if (capturedFrames.length === 0) {
      toast({
        title: "Error",
        description: "No frames captured to save",
        variant: "destructive"
      });
      return;
    }
    
    try {
      addLog(`Saving ${capturedFrames.length} frames to database...`);
      
      const video = videoRef.current;
      const result = await edgeInvoke('pose-save', {
        playback_id: playbackId,
        video_id: selectedVideo?.id || null,
        fps: targetFPS,
        duration_s: video?.duration || null,
        frames: capturedFrames.map(frame => ({
          frame_index: frame.frameIndex,
          time_s: frame.timeS,
          keypoints: frame.landmarks
        }))
      });
      
      setSavedAnalysisId(result.analysis_id);
      addLog(`Saved to database with analysis ID: ${result.analysis_id}`);
      
      toast({
        title: "Success",
        description: `Saved ${result.saved} frames to database`,
      });
      
    } catch (error) {
      addLog(`Failed to save to database: ${error}`);
      toast({
        title: "Error",
        description: "Failed to save to database",
        variant: "destructive"
      });
    }
  };

  const exportJSON = () => {
    if (capturedFrames.length === 0) {
      toast({
        title: "Error",
        description: "No frames captured to export",
        variant: "destructive"
      });
      return;
    }
    
    const exportData = {
      playback_id: playbackId,
      video_id: selectedVideo?.id || null,
      fps: targetFPS,
      frame_count: capturedFrames.length,
      duration_s: videoRef.current?.duration || null,
      captured_at: new Date().toISOString(),
      frames: capturedFrames
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pose_${playbackId}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    addLog(`Exported ${capturedFrames.length} frames to JSON`);
  };

  const sendToAI = async () => {
    if (capturedFrames.length === 0) {
      toast({
        title: "Error",
        description: "No frames captured for AI analysis",
        variant: "destructive"
      });
      return;
    }
    
    try {
      addLog('Sending pose data to OpenAI for analysis...');
      
      const result = await edgeInvoke('pose-analyze', {
        analysis_id: savedAnalysisId || null,
        frames_sample: savedAnalysisId ? null : capturedFrames.slice(0, 50) // Limit for direct analysis
      });
      
      setAiAnalysis(result.analysis);
      addLog(`AI analysis completed for ${result.frames_analyzed} frames`);
      
      toast({
        title: "Success", 
        description: "AI analysis completed",
      });
      
    } catch (error) {
      addLog(`AI analysis failed: ${error}`);
      toast({
        title: "Error",
        description: "Failed to analyze with AI",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 space-y-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-semibold text-foreground mb-2">MediaPipe Golf Pose Analysis</h1>
          <p className="text-muted-foreground">Test pose detection and AI analysis on golf swing videos</p>
        </div>

        <DiagnosticsHeader />

        {/* Video Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <VideoIcon className="h-5 w-5" />
              Video Selection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Playback ID</label>
                <Input 
                  value={playbackId}
                  onChange={(e) => setPlaybackId(e.target.value)}
                  placeholder="Enter Mux playback ID"
                />
              </div>
              <Button onClick={signAndLoadVideo}>Sign & Load Video</Button>
            </div>
            
            {videos.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Or select from your library:</label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {videos
                    .filter(v => v.mux_playback_id && v.status === 'ready')
                    .map(video => (
                    <Button
                      key={video.id}
                      variant={selectedVideo?.id === video.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => loadVideoFromLibrary(video)}
                      className="justify-start text-left p-2 h-auto"
                    >
                      <div className="truncate">
                        <div className="font-medium truncate">{video.mux_playback_id}</div>
                        <div className="text-xs text-muted-foreground">
                          {video.duration ? `${Math.round(video.duration)}s` : ''}
                          {video.width && video.height ? ` • ${video.width}x${video.height}` : ''}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Video Player & Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Video & Pose Overlay</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative bg-black rounded-lg overflow-hidden mb-4">
                <video
                  ref={videoRef}
                  className="w-full h-auto"
                  controls
                  playsInline
                  webkit-playsinline="true"
                  muted
                  preload="metadata"
                  crossOrigin="anonymous"
                />
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  style={{ display: drawSkeleton ? 'block' : 'none' }}
                />
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={startPoseAnalysis}
                  disabled={isPoseRunning || !isVideoLoaded || !mediaPipeReady || !!initError}
                  className="flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                   {isPoseRunning ? 'Capturing Frames...' : 
                    !mediaPipeReady ? 'Initializing MediaPipe...' :
                    !isVideoLoaded ? 'Load Video First' :
                    'Start Pose Analysis'}
                </Button>
                <Button 
                  onClick={stopPoseAnalysis}
                  disabled={!isPoseRunning}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Square className="h-4 w-4" />
                  Stop
                </Button>
                <Button 
                  onClick={saveToDB}
                  disabled={capturedFrames.length === 0}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save to DB
                </Button>
                <Button 
                  onClick={exportJSON}
                  disabled={capturedFrames.length === 0}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export JSON
                </Button>
                <Button 
                  onClick={() => window.open('/pose-analysis', '_blank')}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Saved
                </Button>
                <Button 
                  onClick={() => runAllAnalyses(capturedFrames, playbackId)}
                  disabled={capturedFrames.length === 0 || !playbackId || Object.values(isAnalyzing).some(Boolean)}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  <Brain className="h-4 w-4" />
                  {Object.values(isAnalyzing).some(Boolean) ? 'Running Comparison...' : 'Compare All Methods'}
                </Button>
                <Button 
                  onClick={sendToAI}
                  disabled={capturedFrames.length === 0}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Brain className="h-4 w-4" />
                  Send to OpenAI (Pose Only)
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Analysis Controls & Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Target FPS</label>
                  <Select value={targetFPS.toString()} onValueChange={(v) => setTargetFPS(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 FPS</SelectItem>
                      <SelectItem value="30">30 FPS</SelectItem>
                      <SelectItem value="35">35 FPS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    variant={drawSkeleton ? "default" : "outline"}
                    onClick={() => setDrawSkeleton(!drawSkeleton)}
                    className="w-full"
                  >
                    {drawSkeleton ? 'Hide' : 'Show'} Skeleton
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="mb-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <Badge 
                    variant={processingStatus.includes('Ready') ? 'default' : 
                            processingStatus.includes('failed') || processingStatus.includes('Error') ? 'destructive' : 'secondary'}
                  >
                    {processingStatus}
                  </Badge>
                </div>
                
                {!mediaPipeReady && !initError && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Initialization Progress:</span>
                      <span>{initProgress}%</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${initProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      First-time setup may take 15-30 seconds to download MediaPipe models
                    </p>
                  </div>
                )}
                
                {initError && (
                  <div className="space-y-2">
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                      <p className="text-sm text-destructive font-medium">Initialization Failed</p>
                      <p className="text-xs text-destructive/80 mt-1">{initError}</p>
                    </div>
                    <Button 
                      onClick={retryInitialization}
                      disabled={isRetrying}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      {isRetrying ? 'Retrying...' : 'Retry Initialization'}
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Frames Captured:</span>
                  <Badge variant="secondary" className="ml-2">{processingStats.framesCaptured}</Badge>
                </div>
                <div>
                  <span className="font-medium">Actual FPS:</span>
                  <Badge variant="secondary" className="ml-2">{processingStats.actualFPS.toFixed(1)}</Badge>
                </div>
                <div>
                  <span className="font-medium">Model Load:</span>
                  <Badge variant="secondary" className="ml-2">{processingStats.modelLoadMs.toFixed(0)}ms</Badge>
                </div>
                <div>
                  <span className="font-medium">Avg Latency:</span>
                  <Badge variant="secondary" className="ml-2">{processingStats.avgLatencyMs.toFixed(0)}ms</Badge>
                </div>
              </div>

              {savedAnalysisId && (
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <span className="font-medium">Saved Analysis ID:</span>
                  <code className="ml-2 text-sm bg-background px-2 py-1 rounded">{savedAnalysisId}</code>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Analysis Comparison Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Analysis Comparison
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={clearResults} 
                  variant="outline" 
                  size="sm"
                  disabled={!results.pose && !results.video && !results.combined}
                >
                  Clear Results
                </Button>
                <Button 
                  onClick={exportResults} 
                  variant="outline" 
                  size="sm"
                  disabled={!results.pose && !results.video && !results.combined}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Comparison
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AnalysisComparisonGrid 
              poseResult={results.pose}
              videoResult={results.video}
              combinedResult={results.combined}
              isLoading={isAnalyzing}
            />
          </CardContent>
        </Card>

        {/* Legacy Single AI Analysis Results */}
        {aiAnalysis && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Legacy AI Swing Analysis (Pose Only)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                {aiAnalysis}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Debug Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Debug Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <LogBox text={logs.join('\n')} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}